<script setup lang="ts">
  import { computed, watch, ref, onUnmounted, onMounted } from 'vue'

  import CoursePanelLoader from '@/components/loaders/CoursePanelLoader.vue'
  import CourseDetailsActionLoader from '@/components/loaders/CourseDetailsActionLoader.vue'
  import PlaceholderShimmer from '@/components/common/PlaceholderShimmer.vue'
  import CourseTypeToggle from '@/components/course/course-panel-contents/CourseTypeToggle.vue'
  import CourseProgress from '@/components/course/course-panel-contents/CourseProgress.vue'
  import CourseDetailsAction from '@/components/course/course-panel-contents/CourseDetailsAction.vue'
  import ExamDetails from '@/components/course/course-panel-contents/ExamDetails.vue'
  import WebinarDetails from '@/components/course/course-panel-contents/WebinarDetails.vue'
  import CourseExpiredBanner from '@/components/course/course-panel-contents/CourseExpiredBanner.vue'
  import BuyCourseDetails from '@/components/course/course-panel-contents/BuyCourseDetails.vue'
  import ExamAction from '@/components/course/course-panel-contents/ExamAction.vue'

  import { useRouter } from 'vue-router/composables'
  import useCourse from '@/composables/useCourse'
  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useI18n from '@/composables/useI18n'

  import { CourseModule } from '@/store/modules/course'

  const router = useRouter()
  const {
    course,
    nextModuleToStart,
    isTrialExpired,
    navigateToModule,
    navigateToCourseContent,
    showStartCourseForFreeModal,
  } = useCourse()
  const { cardEmptyImage } = useIcons()
  const { hasValidUser, is34ACompany } = useCommonMixin()
  const { translateString } = useI18n()

  const courseBundle = computed(() => CourseModule.courseBundle)

  const activeCourseType = computed(() => course.value?.courseType || 'written')

  const isCourseContentLoading = ref(false)

  let loadingTimeout: number | null = null

  const loadingContent = computed(() => {
    if (is34ACompany.value) {
      return !course.value?.id
    }
    return !courseBundle.value
  })

  const fetchingCourseModules = computed(() => {
    if (!course.value?.id) return false
    return CourseModule.coursesModuleMap[course.value?.id] === undefined
  })

  const nextLesson = computed(() => {
    if (!nextModuleToStart.value) return null

    return nextModuleToStart.value.lessons.find((lesson) => !lesson.completed) || nextModuleToStart.value.lessons[0]
  })

  const nextActionDisabled = computed(() => {
    if (!hasValidUser.value) return false
    return isTrialExpired.value(course.value) && !course.value?.purchased
  })

  const handleCourseTypeToggle = (type: 'written' | 'oral') => {
    if (courseBundle.value) {
      const selectedCourse = courseBundle.value.courses.find((c) => c.courseType === type)

      if (selectedCourse) {
        CourseModule.setCourse(selectedCourse)
      }
    }
  }

  const handleNextActionClick = () => {
    if (!hasValidUser.value) {
      router.push('/login')
      return
    }

    if (course.value?.purchased || course.value?.expired) {
      navigateToCourseContent(nextModuleToStart.value, nextLesson.value, course.value!.id, false)
    } else if (course.value?.trial) {
      navigateToModule(course.value!.id, nextModuleToStart.value, nextLesson.value)
    } else {
      showStartCourseForFreeModal()
    }
  }

  const handleExamActionClick = async () => {
    if (!hasValidUser.value) {
      router.push('/login')
      return
    }

    if (!course.value?.trial) {
      showStartCourseForFreeModal({
        routeToExam: true,
      })
    } else {
      router.push(`/courses/${course.value?.id}/exam/${course.value?.exam?.id}/start`)
    }
  }

  const fetchCourseModules = (courseId: string) => {
    const courseModules = CourseModule.coursesModuleMap[courseId]

    if (courseModules === undefined) {
      CourseModule.getCourseModules(courseId)
    }
  }

  watch(course, (value) => {
    if (value?.id) {
      fetchCourseModules(value.id)

      isCourseContentLoading.value = true
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
      }

      loadingTimeout = window.setTimeout(() => {
        isCourseContentLoading.value = false
      }, 250)
    }
  })

  onMounted(() => {
    if (course.value?.id) {
      fetchCourseModules(course.value.id)
    }
  })

  onUnmounted(() => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }
  })
</script>

<template lang="pug">
.course-panel-wrapper
  CoursePanelLoader(v-if='loadingContent')
  .course-panel(v-else, :class='{ purchased: course?.purchased }')
    .course-overview-wrapper
      .course-overview
        img.ketch-rounded-normal(:src='course?.cardTeaserImageUrl || course?.teaserImageUrl || cardEmptyImage')
        .course-sector
          h5 {{ $t('courses.ihkExamPreparation') }}
          h3.ketch-font-bold {{ courseBundle?.sector }}
        CourseTypeToggle(
          :active-course-type='activeCourseType',
          @toggle='handleCourseTypeToggle',
          v-if='!is34ACompany'
        )
      CourseProgress(
        :number-of-lessons-completed='course?.numberOfLessonsCompleted || 0',
        :number-of-lessons='course?.numberOfLessons || 0'
      )
      .course-details-action-wrapper
        CourseDetailsActionLoader(v-if='fetchingCourseModules')
        CourseDetailsAction(v-else, :next-action-disabled='nextActionDisabled', @next-action='handleNextActionClick')
    .buy-course-exam-webinar-details(:class='{ purchased: course?.purchased }')
      .loading-wrapper.ketch-h-full(v-if='isCourseContentLoading')
        PlaceholderShimmer(animate, height='100%', width='100%')
      template(v-else)
        .exam-webinar-details(v-if='course?.purchased', :class='{ "has-exam": course?.exam }')
          ExamDetails(v-if='course.exam')
          WebinarDetails(v-if='course.webinar')
        CourseExpiredBanner(v-else-if='course?.expired', on-course-panel)
        .buy-course-package-details-wrapper(v-else, :class='{ "has-exam": course?.exam }')
          BuyCourseDetails
          ExamAction(
            v-if='course?.exam',
            :action-text='translateString("tryForFree")',
            @action='handleExamActionClick'
          )
</template>

<style lang="postcss">
  .course-panel {
    @apply ketch-flex ketch-flex-col ketch-space-y-c20 sm:ketch-shadow-content-shadow sm:ketch-bg-content-background-color;
    @apply sm:ketch-p-c20 sm:ketch-rounded-large sm:ketch-grid sm:ketch-grid-cols-2 sm:ketch-gap-c20 sm:ketch-space-y-0;

    &.purchased {
      @apply ketch-grid-cols-[250px_1fr] sm:ketch-pr-0 md:ketch-grid-cols-[300px_1fr];

      .loading-wrapper {
        @apply sm:ketch-py-c20;
      }
    }

    .course-overview-wrapper {
      @apply ketch-p-c10 ketch-bg-content-background-color ketch-rounded-normal ketch-flex ketch-flex-col ketch-space-y-c20 ketch-shadow-content-shadow;
      @apply xs1:ketch-p-c15 sm:ketch-p-0 sm:ketch-rounded-large sm:ketch-bg-transparent sm:ketch-shadow-none sm:ketch-h-full sm:ketch-justify-between;

      .course-overview {
        @apply ketch-flex ketch-flex-col ketch-space-y-c15;
      }
    }

    .buy-course-exam-webinar-details {
      @apply ketch-flex ketch-flex-col ketch-rounded-large;
      @apply sm:ketch-bg-transparent sm:ketch-rounded-none;

      &.purchased {
        @apply ketch-bg-transparent sm:ketch--my-c20;
      }

      .exam-webinar-details {
        @apply ketch-flex ketch-flex-col ketch-space-y-c20;
        @apply sm:ketch-border-l sm:ketch-border-border-color sm:ketch-space-y-0;

        &.has-exam {
          @apply ketch-h-full;
        }
      }

      .buy-course-package-details-wrapper {
        @apply ketch-flex ketch-flex-col ketch-space-y-c20;

        &:not(.has-exam) {
          @apply ketch-h-full;

          .buy-course-details {
            @apply ketch-h-full ketch-justify-between;
          }
        }
      }
    }
  }
</style>
